import { SATQuestion } from '../../algebra-practice/data/algebraQuestions';

export interface MockExamQuestion extends SATQuestion {
  domain: 'Algebra' | 'Advanced Math' | 'Problem-Solving & Data Analysis' | 'Geometry & Trigonometry';
}

export const mockExamQuestions: MockExamQuestion[] = [
  // ── ALGEBRA (11 questions) ──────────────────────────────────────────────────
  {
    id: 1,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 3x + 7 = 22, what is the value of x?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '5' },
      { label: 'C', value: '7' },
      { label: 'D', value: '9' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['3x + 7 = 22', 'Subtract 7: 3x = 15', 'Divide by 3: x = 5'],
      explanation: 'Isolate x by subtracting 7 then dividing by 3.',
    },
  },
  {
    id: 2,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 2(x − 4) = 3x + 1, what is the value of x?',
    options: [
      { label: 'A', value: '-9' },
      { label: 'B', value: '-7' },
      { label: 'C', value: '7' },
      { label: 'D', value: '9' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Distribute: 2x − 8 = 3x + 1', 'Subtract 2x: −8 = x + 1', 'Subtract 1: x = −9'],
      explanation: 'Expand the left side, then collect x terms on one side.',
    },
  },
  {
    id: 3,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'A line passes through (2, 5) and (6, 13). What is the slope?',
    options: [
      { label: 'A', value: '1/2' },
      { label: 'B', value: '2' },
      { label: 'C', value: '3' },
      { label: 'D', value: '4' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['m = (y₂ − y₁)/(x₂ − x₁)', 'm = (13 − 5)/(6 − 2)', 'm = 8/4 = 2'],
      explanation: 'Slope = rise over run between the two given points.',
    },
  },
  {
    id: 4,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'What is the y-intercept of the line 4x − 2y = 12?',
    options: [
      { label: 'A', value: '-6' },
      { label: 'B', value: '-3' },
      { label: 'C', value: '3' },
      { label: 'D', value: '6' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Solve for y: −2y = −4x + 12', 'Divide by −2: y = 2x − 6', 'y-intercept = −6'],
      explanation: 'Convert to slope-intercept form y = mx + b; b is the y-intercept.',
    },
  },
  {
    id: 5,
    domain: 'Algebra',
    subtopic: 'Linear Inequalities',
    question: 'If 5x − 3 &lt; 17, which value could be x?',
    options: [
      { label: 'A', value: '5' },
      { label: 'B', value: '4' },
      { label: 'C', value: '3' },
      { label: 'D', value: '2' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: ['5x − 3 &lt; 17', 'Add 3: 5x &lt; 20', 'Divide by 5: x &lt; 4', 'Only x = 2 satisfies x &lt; 4'],
      explanation: 'Solve the inequality; any value strictly less than 4 works.',
    },
  },
  {
    id: 6,
    domain: 'Algebra',
    subtopic: 'Systems of Equations',
    question: 'What is the solution (x, y) to: x + y = 8 and x − y = 2?',
    options: [
      { label: 'A', value: '(3, 5)' },
      { label: 'B', value: '(4, 4)' },
      { label: 'C', value: '(5, 3)' },
      { label: 'D', value: '(6, 2)' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Add equations: 2x = 10 → x = 5', 'Substitute: 5 + y = 8 → y = 3'],
      explanation: 'Elimination: add the two equations to cancel y, then back-substitute.',
    },
  },
  {
    id: 7,
    domain: 'Algebra',
    subtopic: 'Systems of Equations',
    question: 'If 2x + y = 10 and 3x − y = 5, what is x?',
    options: [
      { label: 'A', value: '2' },
      { label: 'B', value: '3' },
      { label: 'C', value: '4' },
      { label: 'D', value: '5' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Add equations: 5x = 15', 'Divide by 5: x = 3'],
      explanation: 'Adding the equations eliminates y immediately.',
    },
  },
  {
    id: 8,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'Which equation has slope −3 and passes through (1, 4)?',
    options: [
      { label: 'A', value: 'y = −3x + 1' },
      { label: 'B', value: 'y = −3x + 4' },
      { label: 'C', value: 'y = −3x + 7' },
      { label: 'D', value: 'y = −3x − 1' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['y − 4 = −3(x − 1)', 'y − 4 = −3x + 3', 'y = −3x + 7'],
      explanation: 'Use point-slope form, then convert to slope-intercept form.',
    },
  },
  {
    id: 9,
    domain: 'Algebra',
    subtopic: 'Linear Inequalities',
    question: 'Which inequality represents all x where 2x + 5 ≥ 3x − 1?',
    options: [
      { label: 'A', value: 'x ≤ 4' },
      { label: 'B', value: 'x ≤ 6' },
      { label: 'C', value: 'x ≥ 4' },
      { label: 'D', value: 'x ≥ 6' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['2x + 5 ≥ 3x − 1', 'Subtract 2x: 5 ≥ x − 1', 'Add 1: 6 ≥ x → x ≤ 6'],
      explanation: 'Collect x terms on one side; the inequality direction stays the same.',
    },
  },
  {
    id: 10,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 5x − 2 = 3x + 10, what is the value of 2x?',
    options: [
      { label: 'A', value: '6' },
      { label: 'B', value: '8' },
      { label: 'C', value: '10' },
      { label: 'D', value: '12' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: ['5x − 2 = 3x + 10', 'Subtract 3x: 2x − 2 = 10', 'Add 2: 2x = 12'],
      explanation: 'The question asks for 2x directly — no need to divide further.',
    },
  },
  {
    id: 11,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'Two lines are perpendicular. One has slope 2/5. What is the slope of the other?',
    options: [
      { label: 'A', value: '−5/2' },
      { label: 'B', value: '−2/5' },
      { label: 'C', value: '2/5' },
      { label: 'D', value: '5/2' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Perpendicular slopes are negative reciprocals', 'Flip 2/5 → 5/2, then negate → −5/2'],
      explanation: 'For perpendicular lines, m₁ × m₂ = −1.',
    },
  },

  // ── ADVANCED MATH (11 questions) ───────────────────────────────────────────
  {
    id: 12,
    domain: 'Advanced Math',
    subtopic: 'Quadratic Equations',
    question: 'What are the solutions to x² − 5x + 6 = 0?',
    options: [
      { label: 'A', value: 'x = 2 and x = 3' },
      { label: 'B', value: 'x = −2 and x = −3' },
      { label: 'C', value: 'x = 1 and x = 6' },
      { label: 'D', value: 'x = −1 and x = −6' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Factor: (x − 2)(x − 3) = 0', 'x − 2 = 0 → x = 2', 'x − 3 = 0 → x = 3'],
      explanation: 'Find two numbers that multiply to 6 and add to −5: those are −2 and −3.',
    },
  },
  {
    id: 13,
    domain: 'Advanced Math',
    subtopic: 'Vertex Form',
    question: 'What is the vertex of the parabola y = (x − 4)² + 3?',
    options: [
      { label: 'A', value: '(4, 3)' },
      { label: 'B', value: '(−4, 3)' },
      { label: 'C', value: '(4, −3)' },
      { label: 'D', value: '(−4, −3)' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Vertex form: y = (x − h)² + k', 'h = 4, k = 3', 'Vertex = (4, 3)'],
      explanation: 'In vertex form y = (x − h)² + k, the vertex is directly (h, k).',
    },
  },
  {
    id: 14,
    domain: 'Advanced Math',
    subtopic: 'Polynomials',
    question: 'Simplify: (3x² + 2x − 5) − (x² − 4x + 3)',
    options: [
      { label: 'A', value: '2x² + 6x − 8' },
      { label: 'B', value: '2x² − 2x − 2' },
      { label: 'C', value: '4x² + 6x − 8' },
      { label: 'D', value: '2x² + 6x − 2' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Distribute the negative: 3x² + 2x − 5 − x² + 4x − 3',
        'Combine like terms: 2x² + 6x − 8',
      ],
      explanation: 'Distribute the minus sign to every term in the second polynomial, then combine.',
    },
  },
  {
    id: 15,
    domain: 'Advanced Math',
    subtopic: 'Exponential Functions',
    question: 'If f(x) = 3(2)ˣ, what is f(4)?',
    options: [
      { label: 'A', value: '48' },
      { label: 'B', value: '24' },
      { label: 'C', value: '32' },
      { label: 'D', value: '12' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['f(4) = 3 × 2⁴', '= 3 × 16', '= 48'],
      explanation: 'Substitute x = 4 and evaluate the exponent first, then multiply.',
    },
  },
  {
    id: 16,
    domain: 'Advanced Math',
    subtopic: 'Exponential Functions',
    question: 'Solve for x: 2ˣ = 32',
    options: [
      { label: 'A', value: '4' },
      { label: 'B', value: '5' },
      { label: 'C', value: '6' },
      { label: 'D', value: '16' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Rewrite 32 as a power of 2: 32 = 2⁵', '2ˣ = 2⁵ → x = 5'],
      explanation: 'Express both sides with the same base; then equate the exponents.',
    },
  },
  {
    id: 17,
    domain: 'Advanced Math',
    subtopic: 'Rational Expressions',
    question: 'Simplify: (x² − 9) / (x + 3)',
    options: [
      { label: 'A', value: 'x − 3' },
      { label: 'B', value: 'x + 3' },
      { label: 'C', value: 'x² − 3' },
      { label: 'D', value: '(x − 3)/(x + 3)' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Factor numerator: (x + 3)(x − 3)', 'Cancel (x + 3): result = x − 3, x ≠ −3'],
      explanation: 'Difference of squares: a² − b² = (a + b)(a − b). Cancel the common factor.',
    },
  },
  {
    id: 18,
    domain: 'Advanced Math',
    subtopic: 'Radical Equations',
    question: 'Solve for x: √(x + 5) = 4',
    options: [
      { label: 'A', value: '9' },
      { label: 'B', value: '11' },
      { label: 'C', value: '16' },
      { label: 'D', value: '21' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Square both sides: x + 5 = 16', 'Subtract 5: x = 11', 'Check: √16 = 4 ✓'],
      explanation: 'Square both sides to remove the radical, then solve and verify.',
    },
  },
  {
    id: 19,
    domain: 'Advanced Math',
    subtopic: 'Absolute Value Equations',
    question: 'Solve for x: |2x − 3| = 7',
    options: [
      { label: 'A', value: 'x = 5 or x = −2' },
      { label: 'B', value: 'x = 5 or x = 2' },
      { label: 'C', value: 'x = −5 or x = 2' },
      { label: 'D', value: 'x = 5 only' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Case 1: 2x − 3 = 7 → 2x = 10 → x = 5',
        'Case 2: 2x − 3 = −7 → 2x = −4 → x = −2',
      ],
      explanation: 'An absolute value equation splits into two cases: positive and negative.',
    },
  },
  {
    id: 20,
    domain: 'Advanced Math',
    subtopic: 'Function Notation',
    question: 'If f(x) = 2x² − 3x + 1, what is f(−2)?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '7' },
      { label: 'C', value: '11' },
      { label: 'D', value: '15' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: ['f(−2) = 2(−2)² − 3(−2) + 1', '= 2(4) + 6 + 1', '= 8 + 6 + 1 = 15'],
      explanation: 'Substitute x = −2 carefully, remembering (−2)² = 4.',
    },
  },
  {
    id: 21,
    domain: 'Advanced Math',
    subtopic: 'Polynomials',
    question: 'Factor completely: x³ − 8',
    options: [
      { label: 'A', value: '(x − 2)(x² + 2x + 4)' },
      { label: 'B', value: '(x − 2)(x² − 2x + 4)' },
      { label: 'C', value: '(x + 2)(x² − 2x + 4)' },
      { label: 'D', value: '(x − 2)³' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Difference of cubes: a³ − b³ = (a − b)(a² + ab + b²)', 'x³ − 2³ = (x − 2)(x² + 2x + 4)'],
      explanation: 'Recognize the difference-of-cubes pattern and apply the formula directly.',
    },
  },
  {
    id: 22,
    domain: 'Advanced Math',
    subtopic: 'Quadratic Formula',
    question: 'Use the quadratic formula to solve 2x² + 7x + 3 = 0.',
    options: [
      { label: 'A', value: 'x = −3 and x = −1/2' },
      { label: 'B', value: 'x = 3 and x = 1/2' },
      { label: 'C', value: 'x = −3 and x = 1/2' },
      { label: 'D', value: 'x = 3 and x = −1/2' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'a = 2, b = 7, c = 3',
        'x = [−7 ± √(49 − 24)] / 4 = [−7 ± 5] / 4',
        'x = −2/4 = −1/2  or  x = −12/4 = −3',
      ],
      explanation: 'Apply x = (−b ± √(b²−4ac)) / 2a and simplify both roots.',
    },
  },

  // ── PROBLEM-SOLVING & DATA ANALYSIS (11 questions) ─────────────────────────
  {
    id: 23,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Ratios & Proportions',
    question: 'A recipe uses 3 cups of flour for every 2 cups of sugar. If 12 cups of flour are used, how many cups of sugar are needed?',
    options: [
      { label: 'A', value: '6' },
      { label: 'B', value: '8' },
      { label: 'C', value: '9' },
      { label: 'D', value: '10' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Proportion: 3/2 = 12/x', 'Cross-multiply: 3x = 24', 'x = 8'],
      explanation: 'Set up a proportion and cross-multiply to find the unknown.',
    },
  },
  {
    id: 24,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Percent Problems',
    question: 'A jacket costs $80 and is 35% off. What is the sale price?',
    options: [
      { label: 'A', value: '$28' },
      { label: 'B', value: '$45' },
      { label: 'C', value: '$52' },
      { label: 'D', value: '$56' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Discount = 35% × $80 = $28', 'Sale price = $80 − $28 = $52'],
      explanation: 'Multiply the original price by the discount rate, then subtract.',
    },
  },
  {
    id: 25,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Percent Problems',
    question: 'Revenue increased from $40,000 to $52,000. What is the percent increase?',
    options: [
      { label: 'A', value: '20%' },
      { label: 'B', value: '25%' },
      { label: 'C', value: '30%' },
      { label: 'D', value: '35%' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Change = 52,000 − 40,000 = 12,000', 'Percent = 12,000 / 40,000 × 100 = 30%'],
      explanation: 'Percent change = (change ÷ original) × 100. Always divide by the original.',
    },
  },
  {
    id: 26,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'Test scores for 7 students: 72, 85, 90, 68, 95, 85, 78. What is the median?',
    options: [
      { label: 'A', value: '78' },
      { label: 'B', value: '82' },
      { label: 'C', value: '85' },
      { label: 'D', value: '90' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Ordered: 68, 72, 78, 85, 85, 90, 95', '7 values → median is the 4th value = 85'],
      explanation: 'Sort the data; the median is the middle value for an odd count.',
    },
  },
  {
    id: 27,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'A data set has values: 4, 7, 7, 9, 13. Which statement is true?',
    options: [
      { label: 'A', value: 'Mean &gt; Median' },
      { label: 'B', value: 'Mean = Median' },
      { label: 'C', value: 'Mean &lt; Median' },
      { label: 'D', value: 'Mode &gt; Mean' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Mean = (4+7+7+9+13)/5 = 40/5 = 8', 'Median = 7 (middle value)', 'Mean (8) &gt; Median (7)'],
      explanation: 'The outlier 13 pulls the mean above the median — a right-skewed distribution.',
    },
  },
  {
    id: 28,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Data Interpretation',
    question: 'A line of best fit is y = 5x + 55. What score does it predict for 8 hours of study?',
    options: [
      { label: 'A', value: '85' },
      { label: 'B', value: '90' },
      { label: 'C', value: '95' },
      { label: 'D', value: '100' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Substitute x = 8: y = 5(8) + 55', 'y = 40 + 55 = 95'],
      explanation: 'Plug the x-value into the regression equation to predict y.',
    },
  },
  {
    id: 29,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Probability',
    question: 'A bag has 4 red, 6 blue, and 2 green marbles. What is the probability of NOT drawing blue?',
    options: [
      { label: 'A', value: '1/2' },
      { label: 'B', value: '1/3' },
      { label: 'C', value: '2/3' },
      { label: 'D', value: '3/4' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Total = 12, P(blue) = 6/12 = 1/2', 'P(not blue) = 1 − 1/2 = 1/2'],
      explanation: 'Use the complement rule: P(not A) = 1 − P(A).',
    },
  },
  {
    id: 30,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Ratios & Proportions',
    question: 'On a map, 1 inch = 50 miles. Two cities are 3.5 inches apart. What is the actual distance?',
    options: [
      { label: 'A', value: '150 miles' },
      { label: 'B', value: '175 miles' },
      { label: 'C', value: '200 miles' },
      { label: 'D', value: '225 miles' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Proportion: 1/50 = 3.5/x', 'x = 3.5 × 50 = 175 miles'],
      explanation: 'Multiply the map distance by the scale factor.',
    },
  },
  {
    id: 31,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'A data set has mean = 50 and standard deviation = 5. What value is 2 standard deviations above the mean?',
    options: [
      { label: 'A', value: '55' },
      { label: 'B', value: '58' },
      { label: 'C', value: '60' },
      { label: 'D', value: '65' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['2 SDs above mean = 50 + 2(5)', '= 50 + 10 = 60'],
      explanation: 'Value = mean + (k × SD). Here k = 2.',
    },
  },
  {
    id: 32,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Data Interpretation',
    question: 'A box plot shows Q1 = 25 and Q3 = 55. What is the IQR?',
    options: [
      { label: 'A', value: '15' },
      { label: 'B', value: '25' },
      { label: 'C', value: '30' },
      { label: 'D', value: '70' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['IQR = Q3 − Q1', 'IQR = 55 − 25 = 30'],
      explanation: 'The interquartile range measures the spread of the middle 50% of data.',
    },
  },
  {
    id: 33,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Probability',
    question: 'A fair die is rolled twice. What is the probability of rolling a 4 on both rolls?',
    options: [
      { label: 'A', value: '1/36' },
      { label: 'B', value: '1/18' },
      { label: 'C', value: '1/12' },
      { label: 'D', value: '1/6' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['P(4 on one roll) = 1/6', 'Independent events: P(both) = 1/6 × 1/6 = 1/36'],
      explanation: 'Multiply probabilities for independent events.',
    },
  },

  // ── GEOMETRY & TRIGONOMETRY (11 questions) ─────────────────────────────────
  {
    id: 34,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Area & Perimeter',
    question: 'A rectangle has length 14 cm and width 9 cm. What is its area?',
    options: [
      { label: 'A', value: '46 cm²' },
      { label: 'B', value: '92 cm²' },
      { label: 'C', value: '126 cm²' },
      { label: 'D', value: '144 cm²' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Area = length × width', 'A = 14 × 9 = 126 cm²'],
      explanation: 'Rectangle area = l × w.',
    },
  },
  {
    id: 35,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Pythagorean Theorem',
    question: 'A right triangle has legs 8 and 15. What is the hypotenuse?',
    options: [
      { label: 'A', value: '13' },
      { label: 'B', value: '17' },
      { label: 'C', value: '19' },
      { label: 'D', value: '23' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['a² + b² = c²', '64 + 225 = c²', 'c² = 289 → c = 17'],
      explanation: 'Recognize the 8-15-17 Pythagorean triple.',
    },
  },
  {
    id: 36,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Special Right Triangles',
    question: 'In a 45-45-90 triangle, each leg = 7. What is the hypotenuse?',
    options: [
      { label: 'A', value: '7' },
      { label: 'B', value: '7√2' },
      { label: 'C', value: '7√3' },
      { label: 'D', value: '14' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['45-45-90 ratio: 1 : 1 : √2', 'Hypotenuse = 7 × √2 = 7√2'],
      explanation: 'Multiply the leg by √2 to get the hypotenuse in a 45-45-90 triangle.',
    },
  },
  {
    id: 37,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Trigonometry',
    question: 'In a right triangle, θ = 30° and hypotenuse = 20. What is the side opposite θ?',
    options: [
      { label: 'A', value: '8' },
      { label: 'B', value: '10' },
      { label: 'C', value: '10√3' },
      { label: 'D', value: '20√3' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['sin(30°) = opposite / hypotenuse', '0.5 = opposite / 20', 'opposite = 10'],
      explanation: 'SOH: sin = opposite/hypotenuse. sin(30°) = 1/2.',
    },
  },
  {
    id: 38,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'What is the distance between (1, 2) and (4, 6)?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '4' },
      { label: 'C', value: '5' },
      { label: 'D', value: '7' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['d = √((4−1)² + (6−2)²)', '= √(9 + 16) = √25 = 5'],
      explanation: 'Distance formula — recognize the 3-4-5 right triangle.',
    },
  },
  {
    id: 39,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'What is the midpoint of (−2, 4) and (8, −6)?',
    options: [
      { label: 'A', value: '(3, −1)' },
      { label: 'B', value: '(3, 1)' },
      { label: 'C', value: '(5, −1)' },
      { label: 'D', value: '(6, −2)' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['M = ((−2+8)/2, (4+(−6))/2)', '= (6/2, −2/2) = (3, −1)'],
      explanation: 'Midpoint = average of x-coordinates, average of y-coordinates.',
    },
  },
  {
    id: 40,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Volume',
    question: 'A cylinder has radius 3 and height 10. What is its volume? (π ≈ 3.14)',
    options: [
      { label: 'A', value: '94.2' },
      { label: 'B', value: '188.4' },
      { label: 'C', value: '282.6' },
      { label: 'D', value: '376.8' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['V = πr²h = π × 9 × 10 = 90π', '≈ 3.14 × 90 = 282.6'],
      explanation: 'Cylinder volume = πr²h.',
    },
  },
  {
    id: 41,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Lines & Angles',
    question: 'The interior angles of a triangle are in ratio 2:3:4. What is the largest angle?',
    options: [
      { label: 'A', value: '60°' },
      { label: 'B', value: '70°' },
      { label: 'C', value: '80°' },
      { label: 'D', value: '90°' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['2x + 3x + 4x = 180°', '9x = 180° → x = 20°', 'Largest = 4x = 80°'],
      explanation: 'Triangle angles sum to 180°. Solve for x then find the largest part.',
    },
  },
  {
    id: 42,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Area & Perimeter',
    question: 'A trapezoid has parallel bases 8 and 14, and height 5. What is its area?',
    options: [
      { label: 'A', value: '45' },
      { label: 'B', value: '55' },
      { label: 'C', value: '70' },
      { label: 'D', value: '110' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['A = ½(b₁ + b₂)h', '= ½(8 + 14)(5)', '= ½ × 22 × 5 = 55'],
      explanation: 'Trapezoid area = half the sum of the bases times the height.',
    },
  },
  {
    id: 43,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Trigonometry',
    question: 'If sin(θ) = cos(θ), which must be true?',
    options: [
      { label: 'A', value: 'θ = 30°' },
      { label: 'B', value: 'θ = 45°' },
      { label: 'C', value: 'θ = 60°' },
      { label: 'D', value: 'θ = 90°' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['sin(θ) = cos(θ) → tan(θ) = 1', 'tan(45°) = 1 → θ = 45°'],
      explanation: 'sin = cos only at 45°, where both equal √2/2.',
    },
  },
  {
    id: 44,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'x² + y² − 6x + 4y − 12 = 0 represents a circle. What is its radius?',
    options: [
      { label: 'A', value: '4' },
      { label: 'B', value: '5' },
      { label: 'C', value: '6' },
      { label: 'D', value: '7' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Complete the square: (x−3)² − 9 + (y+2)² − 4 − 12 = 0',
        '(x−3)² + (y+2)² = 25',
        'r = √25 = 5',
      ],
      explanation: 'Complete the square for x and y to get standard form; r = √(right-hand side).',
    },
  },
];

export const DOMAIN_COLORS: Record<MockExamQuestion['domain'], string> = {
  'Algebra': 'emerald',
  'Advanced Math': 'teal',
  'Problem-Solving & Data Analysis': 'cyan',
  'Geometry & Trigonometry': 'amber',
};

export const DOMAIN_QUESTION_COUNTS: Record<MockExamQuestion['domain'], number> = {
  'Algebra': 11,
  'Advanced Math': 11,
  'Problem-Solving & Data Analysis': 11,
  'Geometry & Trigonometry': 11,
};
